/**
 * Il nastro dei fotogrammi: dove sta questo scatto nel volo (GI-265).
 *
 * **Risponde a una domanda che il nastro dei lease non fa: che cosa c'e'
 * accanto.** La coda porta i candidati del batch, che stanno dove il modello
 * li ha trovati — cinque fotogrammi sparsi lungo la linea. Ma un'anomalia si
 * giudica anche dallo scatto prima e da quello dopo: lo stesso isolatore da
 * un altro angolo, il ramo che nel fotogramma seguente non tocca piu'. Il
 * banco precedente (GridInspect) lo faceva con una pellicola di miniature in
 * ordine di volo e un contatore «12 / 78», ed era la cosa che gli operatori
 * rimpiangevano di piu'.
 *
 * **Della stessa camera, in ordine di nome.** I nomi dei file seguono la
 * progressione del volo; due camere che guardano in due direzioni si alternano
 * nel nome, e un nastro che le mescolasse farebbe saltare lo sguardo avanti e
 * indietro a ogni passo. Il servizio manda una finestra attorno al fotogramma
 * corrente con posizione e totale (`search/frames/{id}/strip`).
 *
 * **Ogni miniatura dice che cosa c'e' sopra, con glifo e numero.** `◇` i
 * candidati del modello, `✓` le anomalie registrate, `●` se qualcuno ha
 * dichiarato di averlo guardato. Il colore da solo non dice niente a circa un
 * operatore su dodici.
 *
 * **Orienta e non assegna.** Come il nastro dei lease: un clic porta lo sguardo
 * su quel fotogramma. Se il fotogramma ha un candidato nella coda si apre il
 * candidato; se non ne ha, si guarda il fotogramma con le anomalie gia'
 * registrate, e i verdetti restano spenti con la ragione. Nessun gesto qui
 * acquisisce lavoro.
 */
import { useEffect, useRef } from 'react'

import { useT } from '../i18n/LocaleProvider'
import type { Frame, FrameStrip as Strip } from '../types'

export interface FrameStripProps {
  strip: Strip | null
  /** Il fotogramma che la tela mostra adesso, per identita'. */
  shownId: string | null
  /** Vero mentre il servizio non ha ancora risposto per questo fotogramma. */
  reading: boolean
  closed: boolean
  onGo: (frame: Frame) => void
  /** I due passi, gli stessi di `H` e `L`: il bottone e il tasto producono la
   *  stessa azione. */
  onStep: (step: -1 | 1) => void
}

export function FrameStrip({
  strip,
  shownId,
  reading,
  closed,
  onGo,
  onStep,
}: FrameStripProps): JSX.Element {
  const t = useT()
  const list = useRef<HTMLOListElement>(null)

  const where = strip === null || shownId === null
    ? -1
    : strip.frames.findIndex((one) => one.photo_id === shownId)
  const current = where < 0 ? null : (strip?.frames[where] ?? null)
  const index = strip === null || where < 0 ? null : strip.offset + where

  // Il fotogramma corrente sta sempre in vista: il nastro scorre da solo, come
  // farebbe una mano che lo cerca. Sotto jsdom `scrollIntoView` non c'e'.
  useEffect(() => {
    const node = list.current?.querySelector<HTMLElement>('[aria-current="true"]')
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', inline: 'center' })
    }
  }, [shownId, strip])

  return (
    <nav
      className="cockpit-pellicola"
      aria-label={t('cockpit.frameStrip')}
      data-closed={closed || undefined}
    >
      <h2 className="solo-lettori">{t('cockpit.frameStrip')}</h2>
      {/* Una riga sola (GI-266): il passo indietro, le miniature, il passo
          avanti, e a destra dove si e' — il numero grande, perche' e' la
          risposta alla domanda che il nastro esiste per dare, e sotto la
          camera e che cosa c'e' su questo fotogramma. */}
      <button
        type="button"
        className="cockpit-pellicola-passo"
        aria-label={`${t('keys.previousFrame')} (H)`}
        title={`${t('keys.previousFrame')} · H`}
        disabled={index === null || index <= 0}
        onClick={() => onStep(-1)}
      >
        <span aria-hidden="true">‹</span>
        <kbd aria-hidden="true">H</kbd>
      </button>
      {strip !== null && strip.frames.length > 0 ? (
        <ol className="cockpit-pellicola-nastro" ref={list}>
          {strip.frames.map((frame, position) => {
            const on = frame.photo_id === shownId
            const candidates = frame.candidates ?? 0
            return (
              <li key={frame.photo_id} aria-current={on ? 'true' : undefined}>
                <button
                  type="button"
                  className="cockpit-fotogramma"
                  title={frame.filename}
                  // Il nome accessibile dice posizione, file e che cosa c'e'
                  // sopra: chi ascolta non vede la miniatura ne' i glifi.
                  aria-label={[
                    t('cockpit.frameOf', { n: strip.offset + position + 1, total: strip.total }),
                    frame.filename,
                    t('cockpit.frameCandidates', { n: candidates }),
                    t('cockpit.frameFindings', { n: frame.findings }),
                  ].join(', ')}
                  onClick={() => onGo(frame)}
                >
                  {frame.thumbnail_url !== null ? (
                    <img
                      className="cockpit-fotogramma-img"
                      src={frame.thumbnail_url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="cockpit-fotogramma-vuoto" aria-hidden="true" />
                  )}
                  <span className="cockpit-fotogramma-segni" aria-hidden="true">
                    {candidates > 0 && <span className="segno-candidati">◇{candidates}</span>}
                    {frame.findings > 0 && <span className="segno-anomalie">✓{frame.findings}</span>}
                    {frame.reviewed === true && <span className="segno-visto">●</span>}
                  </span>
                  <span className="cockpit-fotogramma-n t-num" aria-hidden="true">
                    {strip.offset + position + 1}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
      ) : (
        <div className="cockpit-pellicola-nastro cockpit-pellicola-vuota" aria-hidden="true" />
      )}
      <button
        type="button"
        className="cockpit-pellicola-passo"
        aria-label={`${t('keys.nextFrame')} (L)`}
        title={`${t('keys.nextFrame')} · L`}
        disabled={strip === null || index === null || index >= strip.total - 1}
        onClick={() => onStep(1)}
      >
        <kbd aria-hidden="true">L</kbd>
        <span aria-hidden="true">›</span>
      </button>
      <p className="cockpit-pellicola-dove" role="status">
        {strip === null || index === null ? (
          <span className="tenue">
            {reading ? t('cockpit.stripReading') : t('cockpit.stripEmpty')}
          </span>
        ) : (
          <>
            <span className="cockpit-pellicola-n t-num">
              {t('cockpit.frameOf', { n: index + 1, total: strip.total })}
            </span>
            <span className="cockpit-pellicola-sotto">
              {strip.camera !== null && (
                <span className="cockpit-pellicola-camera">
                  {t('cockpit.hudCamera', { name: strip.camera })}
                </span>
              )}
              {current !== null && (
                <span className="tenue">
                  {strip.camera !== null && ' · '}
                  <span aria-hidden="true">◇ </span>
                  {t('cockpit.frameCandidates', { n: current.candidates ?? 0 })}
                  {' · '}
                  <span aria-hidden="true">✓ </span>
                  {t('cockpit.frameFindings', { n: current.findings })}
                  {' · '}
                  {current.reviewed === true ? t('cockpit.frameReviewed') : t('cockpit.frameUnreviewed')}
                </span>
              )}
            </span>
          </>
        )}
      </p>
    </nav>
  )
}
